import React, { useState } from "react";

import { css, keyframes } from "@compiled/react";

import { wacToken } from "@/libraries/design/foundations";

import { RovoRainbowBorder } from "./spiderweb-rovo-rainbow-border";
import {
    type FillColor,
    type SpiderWebNodeFocusVariant,
    type WebNodeConfig
} from "./spiderweb-types";
import {
    getFillColor,
    getHexagonPoints,
    getRectangleBorderRadius,
    getTrianglePoints,
    pctToViewBox,
    pctToViewBoxY,
    type NodeCenterPct,
    VIEWBOX_SCALE
} from "./spiderweb-utils";

import type { AspectCorrection } from "./use-spiderweb-viewport-aspect";

type WebNodeProps = {
    node: WebNodeConfig;
    animated: boolean;
    delay: number;
    loadEntranceDurationSec?: number;
    skipLoadEntrance?: boolean;
    onClick?: () => void;
    fontFamily?: string;
    isExpanding?: boolean;
    viewBoxHeight?: number;
    aspectCorrection?: AspectCorrection;
    layoutPct?: NodeCenterPct;
    focusVariant?: SpiderWebNodeFocusVariant;
};

const activeSelectionKeyframes = keyframes({
    "0%": {
        outlineOffset: "10px"
    },
    "100%": {
        outlineOffset: "0px"
    }
});

const groupStyle = css({
    "&:focus:not(:focus-visible), &.spiderweb-node--focus-connector:focus": {
        outline: "none"
    },

    "&.spiderweb-node--focus-node-edit, &.spiderweb-node--focus-node-edit:focus":
        {
            outline: "10px dashed hotpink",
            animationName: activeSelectionKeyframes,
            animationDuration: "0.5s",
            animationIterationCount: "infinite",
            animationTimingFunction: "steps(2)"
        },

    ["&.modal-interactive"]: {
        cursor: "pointer"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["foreignObject"]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["p, h2, h3, h4, h5, h6, a"]: {
            color: "var(--node-text-color)",
            margin: "0 0 0.5rem"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["h2"]: {
            fontWeight: 400
        }
    }
});

const DEFAULT_SIZE = 100;
const PILL_ASPECT = 2.8;
const PILL_HORIZONTAL_PADDING = 0.8;
const RECTANGLE_HORIZONTAL_PADDING = 0.8;
const MONOSPACE_CHAR_WIDTH_RATIO = 0.6;

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
}

/* eslint-disable complexity */
const DEFAULT_LOAD_ENTRANCE_DURATION_SEC = 0.6;

export const WebNode = ({
    node,
    animated,
    delay,
    loadEntranceDurationSec = DEFAULT_LOAD_ENTRANCE_DURATION_SEC,
    skipLoadEntrance = false,
    fontFamily = "monospace",
    onClick,
    isExpanding = false,
    viewBoxHeight,
    aspectCorrection,
    layoutPct,
    focusVariant
}: WebNodeProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const px = layoutPct?.x ?? node.x;
    const py = layoutPct?.y ?? node.y;
    const outerOpacity = clamp01(layoutPct?.opacity ?? node.opacity ?? 1);
    const cx = pctToViewBox(px);
    const cy = pctToViewBoxY(py, viewBoxHeight ?? VIEWBOX_SCALE);
    const size = layoutPct?.size ?? node.size ?? DEFAULT_SIZE;
    const r = size / 2;
    const hasModal = !!node.modalContent;
    const patternId = `img-${node.id}`;
    const clipId = `clip-${node.id}`;

    const fontSize = size * 0.3;
    const pillWidth =
        layoutPct?.width ??
        (node.shape === "pill" && node.text
            ? node.text.length * fontSize * MONOSPACE_CHAR_WIDTH_RATIO +
              size * PILL_HORIZONTAL_PADDING
            : size * PILL_ASPECT);
    const rectangleWidth =
        layoutPct?.width ??
        node.width ??
        (node.shape === "rectangle" && node.text
            ? node.text.length * fontSize * MONOSPACE_CHAR_WIDTH_RATIO +
              size * RECTANGLE_HORIZONTAL_PADDING
            : size);
    const rectangleHeight = layoutPct?.height ?? node.height ?? size;

    const rectangleRx = getRectangleBorderRadius(
        node,
        rectangleWidth,
        rectangleHeight
    );

    const rotation = node.rotate ? `rotate(${node.rotate}deg)` : "";

    const tx = !isExpanding && isHovered ? cx + 2 : cx;
    const ty = !isExpanding && isHovered ? cy - 5 : cy;

    const innerScale = isExpanding ? 1.25 : animated ? 1 : 0;

    const acX = aspectCorrection?.scaleX ?? 1;
    const acY = aspectCorrection?.scaleY ?? 1;

    let innerTransition: string | undefined;
    if (isExpanding) {
        innerTransition = "opacity 0.3s ease, transform 0.3s ease";
    } else if (isHovered) {
        innerTransition = "opacity 0.3s ease, transform 0.3s ease";
    } else if (animated) {
        innerTransition = skipLoadEntrance
            ? undefined
            : `opacity ${loadEntranceDurationSec}s ease ${delay}s, transform ${loadEntranceDurationSec}s ease ${delay}s`;
    }

    const positionOuterStyle: React.CSSProperties = {
        transform: `translate(${tx}px, ${ty}px)`,
        transformOrigin: "0 0",
        transition: "none",
        mixBlendMode:
            isExpanding || isHovered ? "normal" : node.mixBlendMode ?? "normal",
        opacity: outerOpacity
    };

    const textColor = getFillColor(node.textColor ?? "white");
    const innerStyle: React.CSSProperties = {
        "transform": `scale(${innerScale * acX}, ${
            innerScale * acY
        }) ${rotation}`,
        "transformOrigin": "0 0",
        "--node-text-color": textColor,
        ...(innerTransition ? { transition: innerTransition } : {})
    } as React.CSSProperties & { "--node-text-color": string };

    const clickable = !!onClick;
    const groupProps = {
        style: {
            cursor: clickable ? "pointer" : "default"
        } as React.CSSProperties,
        onClick: clickable ? () => onClick?.() : undefined,
        role: clickable ? "button" : undefined,
        tabIndex: clickable ? 0 : undefined,
        onKeyDown: clickable
            ? (e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onClick?.();
                  }
              }
            : undefined
    };

    const modalPointerHandlers = hasModal
        ? {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false)
          }
        : {};

    const fill = getFillColor(node.bgColor ?? "blue500");
    const isRovoRainbowBorder = node.borderColor === "rainbowRovo";
    const solidBorderColor: FillColor | undefined =
        node.borderColor && node.borderColor !== "rainbowRovo"
            ? node.borderColor
            : undefined;
    const defaultStrokeWidth = node.borderWidth ?? 2;
    const stroke = solidBorderColor ? getFillColor(solidBorderColor) : "none";
    const strokeWidth = solidBorderColor ? defaultStrokeWidth : 0;
    const rovoRingWidth = node.borderWidth ?? 6;

    const rovoRainbowLayer =
        isRovoRainbowBorder &&
        (node.shape === "pill" || node.shape === "rectangle") ? (
            <RovoRainbowBorder
                nodeId={node.id}
                shape={node.shape === "pill" ? "pill" : "rectangle"}
                innerWidth={node.shape === "pill" ? pillWidth : rectangleWidth}
                innerHeight={node.shape === "pill" ? size : rectangleHeight}
                innerRectangleBorderRadius={
                    node.shape === "rectangle" ? rectangleRx : undefined
                }
                ringWidth={rovoRingWidth}
            />
        ) : null;

    const nodeFontFamily = node.fontFamily ?? fontFamily;
    const typeFace =
        nodeFontFamily === "charlie"
            ? wacToken("font.charlie.display")
            : "monospace";
    const defaultFontWeight = nodeFontFamily === "charlie" ? 600 : 400;
    const textFontWeight = node.fontWeight ?? defaultFontWeight;

    const renderDefs = () => {
        if (!node.bgImage) return null;

        let clipShape: React.ReactNode;
        switch (node.shape) {
            case "circle":
                clipShape = <circle cx={0} cy={0} r={r} />;
                break;
            case "pill":
                clipShape = (
                    <rect
                        x={-pillWidth / 2}
                        y={-r}
                        width={pillWidth}
                        height={size}
                        rx={r}
                    />
                );
                break;
            case "rectangle":
                clipShape = (
                    <rect
                        x={-rectangleWidth / 2}
                        y={-rectangleHeight / 2}
                        width={rectangleWidth}
                        height={rectangleHeight}
                        rx={rectangleRx}
                    />
                );
                break;
            case "hexagon":
                clipShape = <polygon points={getHexagonPoints(0, 0, r)} />;
                break;
            case "triangle":
                clipShape = <polygon points={getTrianglePoints(0, 0, r)} />;
                break;
        }

        const imgW = node.shape === "rectangle" ? rectangleWidth : size;
        const imgH = node.shape === "rectangle" ? rectangleHeight : size;

        return (
            <defs>
                <clipPath id={clipId}>{clipShape}</clipPath>
                <pattern
                    id={patternId}
                    patternUnits="objectBoundingBox"
                    width="1"
                    height="1"
                >
                    <image
                        href={node.bgImage}
                        x={-imgW / 2}
                        y={-imgH / 2}
                        width={imgW}
                        height={imgH}
                        preserveAspectRatio="xMidYMid slice"
                    />
                </pattern>
            </defs>
        );
    };

    const renderShape = () => {
        const clipPath = node.bgImage ? `url(#${clipId})` : undefined;

        switch (node.shape) {
            case "circle":
                return (
                    <>
                        <circle
                            cx={0}
                            cy={0}
                            r={r}
                            fill={fill}
                            stroke={stroke}
                            strokeWidth={strokeWidth}
                            vectorEffect="non-scaling-stroke"
                        />
                        {node.bgImage && (
                            <image
                                href={node.bgImage}
                                x={-r}
                                y={-r}
                                width={size}
                                height={size}
                                clipPath={clipPath}
                                preserveAspectRatio="xMidYMid slice"
                            />
                        )}
                    </>
                );
            case "pill":
                return (
                    <>
                        <rect
                            x={-pillWidth / 2}
                            y={-r}
                            width={pillWidth}
                            height={size}
                            rx={r}
                            fill={fill}
                            stroke={stroke}
                            strokeWidth={strokeWidth}
                            vectorEffect="non-scaling-stroke"
                        />
                        {node.bgImage && (
                            <image
                                href={node.bgImage}
                                x={-pillWidth / 2}
                                y={-r}
                                width={pillWidth}
                                height={size}
                                clipPath={clipPath}
                                preserveAspectRatio="xMidYMid slice"
                            />
                        )}
                    </>
                );
            case "rectangle":
                return (
                    <>
                        <rect
                            x={-rectangleWidth / 2}
                            y={-rectangleHeight / 2}
                            width={rectangleWidth}
                            height={rectangleHeight}
                            rx={rectangleRx}
                            fill={fill}
                            stroke={stroke}
                            strokeWidth={strokeWidth}
                            vectorEffect="non-scaling-stroke"
                        />
                        {node.bgImage && (
                            <image
                                href={node.bgImage}
                                x={-rectangleWidth / 2}
                                y={-rectangleHeight / 2}
                                width={rectangleWidth}
                                height={rectangleHeight}
                                clipPath={clipPath}
                                preserveAspectRatio="xMidYMid slice"
                            />
                        )}
                    </>
                );
            case "hexagon":
                return (
                    <>
                        <polygon
                            points={getHexagonPoints(0, 0, r)}
                            fill={fill}
                            stroke={stroke}
                            strokeWidth={strokeWidth}
                            vectorEffect="non-scaling-stroke"
                        />
                        {node.bgImage && (
                            <image
                                href={node.bgImage}
                                x={-r}
                                y={-r}
                                width={size}
                                height={size}
                                clipPath={clipPath}
                                preserveAspectRatio="xMidYMid slice"
                            />
                        )}
                    </>
                );
            case "triangle":
                return (
                    <>
                        <polygon
                            points={getTrianglePoints(0, 0, r)}
                            fill={fill}
                            stroke={stroke}
                            strokeWidth={strokeWidth}
                            vectorEffect="non-scaling-stroke"
                        />
                        {node.bgImage && (
                            <image
                                href={node.bgImage}
                                x={-r}
                                y={-r}
                                width={size}
                                height={size}
                                clipPath={clipPath}
                                preserveAspectRatio="xMidYMid slice"
                            />
                        )}
                    </>
                );
        }
    };

    const renderText = () => {
        const textSize =
            nodeFontFamily === "monospace" ? size * 0.3 : size * 0.4;

        if (!node.text) return null;

        return (
            <text
                x={0}
                y={0}
                textAnchor="middle"
                dominantBaseline="central"
                fill={textColor}
                fontSize={textSize}
                fontWeight={textFontWeight}
                fontFamily={typeFace}
                pointerEvents="none"
            >
                {node.text}
            </text>
        );
    };

    const renderRectangleHtmlContent = () => {
        if (
            node.shape !== "rectangle" ||
            node.htmlContent === undefined ||
            node.htmlContent === null
        ) {
            return null;
        }
        const w = rectangleWidth;
        const h = rectangleHeight;
        const baseFontPx = Math.max(10, Math.round(size * 0.28));
        const xhtmlWrapperStyle: React.CSSProperties = {
            width: "100%",
            height: "100%",
            margin: 0,
            boxSizing: "border-box",
            padding: "0.35em",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
            overflow: "hidden",
            fontFamily: typeFace,
            fontWeight: textFontWeight,
            color: textColor,
            fontSize: `${baseFontPx}px`,
            lineHeight: 1.35,
            WebkitFontSmoothing: "antialiased"
        };

        return (
            <foreignObject
                x={-w / 2}
                y={-h / 2}
                width={w}
                height={h}
                // pointerEvents="none"
            >
                {React.createElement(
                    "body",
                    {
                        xmlns: "http://www.w3.org/1999/xhtml",
                        style: xhtmlWrapperStyle
                    },
                    node.htmlContent
                )}
            </foreignObject>
        );
    };

    const focusClass =
        focusVariant === "node-edit"
            ? "spiderweb-node--focus-node-edit"
            : focusVariant === "connector"
            ? "spiderweb-node--focus-connector"
            : "";

    return (
        <g
            className={`
        ${hasModal ? "modal-interactive" : ""}
        ${hasModal && isHovered ? "modal-hovered" : ""}
        ${focusClass}
        `}
            id={`node-group-${node.id}`}
            css={groupStyle}
            data-modal-interactive={hasModal ? true : undefined}
            {...groupProps}
            {...modalPointerHandlers}
        >
            <g style={positionOuterStyle}>
                <g style={innerStyle}>
                    {rovoRainbowLayer}
                    {renderDefs()}
                    {renderShape()}
                    {renderRectangleHtmlContent()}
                    {renderText()}
                </g>

                {focusVariant === "connector" && (
                    <>
                        <rect
                            fill="black"
                            width={170}
                            height={40}
                            y={-25}
                            x={-80}
                        />
                        <text fill="white" x={-70}>
                            Select another node
                        </text>
                    </>
                )}
            </g>
        </g>
    );
};
/* eslint-enable complexity */
